import React, { createContext, useContext, useState, useEffect } from 'react';
import { chapters as courseChapters } from '../data/courseContent';

const CourseContext = createContext(null);

export const useCourse = () => {
  const context = useContext(CourseContext);
  if (!context) {
    throw new Error('useCourse must be used within a CourseProvider');
  }
  return context;
};

export const CourseProvider = ({ children }) => {
  // Load saved progress from localStorage
  const loadProgress = () => {
    try {
      const saved = localStorage.getItem('courseProgress');
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  };

  const loadJournalEntries = () => {
    try {
      const saved = localStorage.getItem('journalEntries');
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  };

  const loadBookmarks = () => {
    try {
      const saved = localStorage.getItem('bookmarks');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  };

  const loadCurrentChapter = () => {
    try {
      const saved = localStorage.getItem('currentChapter');
      return saved ? parseInt(saved, 10) : 1;
    } catch {
      return 1;
    }
  };

  const [progress, setProgress] = useState(loadProgress);
  const [journalEntries, setJournalEntries] = useState(loadJournalEntries);
  const [bookmarks, setBookmarks] = useState(loadBookmarks);
  const [currentChapterId, setCurrentChapterId] = useState(loadCurrentChapter);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [audioProgress, setAudioProgress] = useState(0);

  // Save to localStorage whenever state changes
  useEffect(() => {
    localStorage.setItem('courseProgress', JSON.stringify(progress));
  }, [progress]);

  useEffect(() => {
    localStorage.setItem('journalEntries', JSON.stringify(journalEntries));
  }, [journalEntries]);

  useEffect(() => {
    localStorage.setItem('bookmarks', JSON.stringify(bookmarks));
  }, [bookmarks]);

  useEffect(() => {
    localStorage.setItem('currentChapter', currentChapterId.toString());
  }, [currentChapterId]);

  // Calculate overall progress percentage
  const calculateOverallProgress = () => {
    const completedChapters = Object.values(progress).filter(p => p.completed).length;
    return Math.round((completedChapters / courseChapters.length) * 100);
  };

  // Mark chapter as complete
  const markChapterComplete = (chapterId) => {
    setProgress(prev => ({
      ...prev,
      [chapterId]: {
        ...prev[chapterId],
        completed: true,
        completedAt: new Date().toISOString(),
      }
    }));
  };

  // Mark chapter as started
  const markChapterStarted = (chapterId) => {
    setProgress(prev => ({
      ...prev,
      [chapterId]: {
        ...prev[chapterId],
        started: true,
        startedAt: prev[chapterId]?.startedAt || new Date().toISOString(),
      }
    }));
  };

  // Update reading progress within a chapter
  const updateReadingProgress = (chapterId, percentage) => {
    setProgress(prev => ({
      ...prev,
      [chapterId]: {
        ...prev[chapterId],
        readingProgress: percentage,
      }
    }));
  };

  // Save journal entry
  const saveJournalEntry = (chapterId, entry) => {
    setJournalEntries(prev => ({
      ...prev,
      [chapterId]: {
        content: entry,
        savedAt: new Date().toISOString(),
      }
    }));
  };

  // Get journal entry for a chapter
  const getJournalEntry = (chapterId) => {
    return journalEntries[chapterId]?.content || '';
  };

  // Toggle bookmark
  const toggleBookmark = (chapterId) => {
    setBookmarks(prev => {
      if (prev.includes(chapterId)) {
        return prev.filter(id => id !== chapterId);
      }
      return [...prev, chapterId];
    });
  };

  // Check if chapter is bookmarked
  const isBookmarked = (chapterId) => {
    return bookmarks.includes(chapterId);
  };

  // Get chapter progress
  const getChapterProgress = (chapterId) => {
    return progress[chapterId] || { started: false, completed: false, readingProgress: 0 };
  };

  // Navigate to chapter
  const goToChapter = (chapterId) => {
    setCurrentChapterId(chapterId);
    markChapterStarted(chapterId);
  };

  // Get current chapter data
  const getCurrentChapter = () => {
    return courseChapters.find(ch => ch.id === currentChapterId);
  };

  // Get next chapter
  const getNextChapter = () => {
    const currentIndex = courseChapters.findIndex(ch => ch.id === currentChapterId);
    if (currentIndex < courseChapters.length - 1) {
      return courseChapters[currentIndex + 1];
    }
    return null;
  };

  // Get previous chapter
  const getPreviousChapter = () => {
    const currentIndex = courseChapters.findIndex(ch => ch.id === currentChapterId);
    if (currentIndex > 0) {
      return courseChapters[currentIndex - 1];
    }
    return null;
  };

  // Audio controls (mocked)
  const toggleAudio = () => {
    setIsAudioPlaying(prev => !prev);
  };

  const resetAudio = () => {
    setIsAudioPlaying(false);
    setAudioProgress(0);
  };

  // Reset all progress
  const resetProgress = () => {
    setProgress({});
    setJournalEntries({});
    setBookmarks([]);
    setCurrentChapterId(1);
    localStorage.removeItem('courseProgress');
    localStorage.removeItem('journalEntries');
    localStorage.removeItem('bookmarks');
    localStorage.removeItem('currentChapter');
  };

  const value = {
    chapters: courseChapters,
    currentChapterId,
    progress,
    journalEntries,
    bookmarks,
    isAudioPlaying,
    audioProgress,
    overallProgress: calculateOverallProgress(),
    markChapterComplete,
    markChapterStarted,
    updateReadingProgress,
    saveJournalEntry,
    getJournalEntry,
    toggleBookmark,
    isBookmarked,
    getChapterProgress,
    goToChapter,
    getCurrentChapter,
    getNextChapter,
    getPreviousChapter,
    toggleAudio,
    resetAudio,
    setAudioProgress,
    resetProgress,
  };

  return (
    <CourseContext.Provider value={value}>
      {children}
    </CourseContext.Provider>
  );
};

export default CourseContext;
